'use client';

import React, { Component } from 'react';

import type { ErrorInfo, ReactNode } from 'react';

import { isCapacitor } from '@/lib/platformUtils';
import { clearInstalledPwaCaches, recoverServiceWorkerAfterCacheReset } from '@/lib/serviceWorkerRegistration';
import { clearNativeCache } from '@/lib/nativeCacheBridge';
import { reportError } from '@/lib/errorReportingService';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isChunkError: boolean;
  isClearingCaches: boolean;
  isMaintenanceMode: boolean;
  reportReference: string | null;
  isReporting: boolean;
}

/**
 * Check if error is a maintenance mode / 503 error
 */
function isMaintenanceError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  return (
    message.includes('503') ||
    message.includes('maintenance') ||
    message.includes('service unavailable')
  );
}

/**
 * Check if error is a chunk loading error (deployment mismatch)
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  return (
    message.includes('failed to load chunk') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('cannot find module') ||
    message.includes("unexpected token '<") ||
    message.includes('unexpected token "<')
  );
}

/**
 * Clear all caches via Service Worker and native cache bridge
 */
async function clearAllCaches(): Promise<boolean> {
  try {
    // Ask service worker to clear caches
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
      // Wait a bit for SW to process
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Also clear caches directly as backup
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter(name =>
            name.includes('nextjs') ||
            name.includes('totalaccess') ||
            name.includes('pages')
          )
          .map(name => caches.delete(name))
      );
    }

    // Clear native WebView disk cache (Capacitor only)
    clearNativeCache();

    return true;
  } catch (e) {
    console.error('Failed to clear caches:', e);
    return false;
  }
}

/**
 * Error Boundary specifically for Capacitor apps
 * Provides automatic retry logic and better error handling for mobile environment
 * Now with chunk error detection and automatic cache clearing
 */
export class CapacitorErrorBoundary extends Component<Props, State> {
  private retryTimer: NodeJS.Timeout | null = null;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds
  private reportedError: Error | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isChunkError: false,
      isClearingCaches: false,
      isMaintenanceMode: false,
      reportReference: null,
      isReporting: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
      isMaintenanceMode: isMaintenanceError(error),
    };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Capacitor Error Boundary] Caught error:', error);
    console.error('[Capacitor Error Boundary] Error info:', errorInfo);

    const isChunk = isChunkLoadError(error);
    const isMaintenance = isMaintenanceError(error);
    
    this.setState({
      error,
      errorInfo,
      isChunkError: isChunk,
      isMaintenanceMode: isMaintenance,
    });

    // If it looks like a maintenance error, verify with health endpoint
    if (isMaintenance || (!isChunk && typeof window !== 'undefined')) {
      try {
        const resp = await fetch('/api/v1/health', { cache: 'no-store', signal: AbortSignal.timeout(5000) });
        if (resp.status === 503 || (resp.ok && (await resp.json()).status === 'down')) {
          console.log('[Capacitor Error Boundary] 🔧 Maintenance mode confirmed');
          this.setState({ isMaintenanceMode: true });
          // If the native maintenance screen is available, use it
          if (typeof window !== 'undefined' && (window as any).__showMaintenanceScreen) {
            (window as any).__showMaintenanceScreen();
          }
          return; // Don't auto-retry during maintenance
        }
      } catch {
        // Health check failed - could be maintenance
      }
    }

    // If it's a chunk error, automatically clear caches and reload — but only on
    // native platforms (Capacitor). In web dev with Turbopack/HMR, chunk load
    // failures are transient and auto-reloading creates an infinite loop.
    if (isChunk && isCapacitor()) {
      console.log('[Capacitor Error Boundary] 🚨 Chunk loading error detected - clearing caches...');
      this.setState({ isClearingCaches: true });

      const cleared = await clearAllCaches();

      if (cleared) {
        console.log('[Capacitor Error Boundary] ✅ Caches cleared, reloading in 1s...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
    }

    // Auto-retry if in Capacitor and haven't exceeded max retries
    if (typeof window !== 'undefined' && isCapacitor() && !isChunk) {
      const { retryCount } = this.state;

      if (retryCount < this.MAX_RETRIES) {
        console.log(`[Capacitor Error Boundary] Auto-retry ${retryCount + 1}/${this.MAX_RETRIES} in ${this.RETRY_DELAY}ms...`);

        this.retryTimer = setTimeout(() => {
          this.handleRetry();
        }, this.RETRY_DELAY);
      }
    }

    // Auto-report the error (deduplicated by error identity). Chunk errors
    // that auto-reload don't need a report, but all other errors do.
    if (!isChunk && this.reportedError !== error) {
      this.reportedError = error;
      void this.handleReportError(error, errorInfo);
    }
  }

  handleReportError = async (error: Error, errorInfo: ErrorInfo) => {
    this.setState({ isReporting: true });
    try {
      const result = await reportError(
        error,
        'CapacitorErrorBoundary',
        'Error caught by CapacitorErrorBoundary',
        {
          componentStack: errorInfo.componentStack,
        },
      );

      if (result.success && result.referenceNumber) {
        this.setState({ reportReference: result.referenceNumber });
      }
    } catch (err) {
      console.error('[Capacitor Error Boundary] Failed to report error:', err);
    } finally {
      this.setState({ isReporting: false });
    }
  };

  componentWillUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  handleRetry = () => {
    console.log('[Capacitor Error Boundary] Retrying...');

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      reportReference: null,
      isReporting: false,
    }));
  };

  handleReload = () => {
    console.log('[Capacitor Error Boundary] Reloading app...');
    window.location.reload();
  };

  handleClearCacheAndReload = async () => {
    console.log('[Capacitor Error Boundary] Clearing cache and reloading...');
    this.setState({ isClearingCaches: true });
    try {
      await clearInstalledPwaCaches();
      await recoverServiceWorkerAfterCacheReset();
      await new Promise(resolve => setTimeout(resolve, 500));
      window.location.reload();
    } catch (e) {
      console.error('[Capacitor Error Boundary] Clear cache failed:', e);
      this.setState({ isClearingCaches: false });
    }
  };

  render() {
    const { hasError, error, retryCount, isChunkError, isClearingCaches, isMaintenanceMode } = this.state;
    const { children } = this.props;

    if (hasError && error) {
      // For chunk errors, show recovery UI on ALL platforms (web + Capacitor).
      // For other errors, only show the error UI in Capacitor; re-throw on web
      // so Next.js error boundaries can handle it.
      const isChunk = isChunkLoadError(error);
      if (typeof window === 'undefined' || (!isCapacitor() && !isChunk)) {
        throw error;
      }

      // Maintenance mode UI
      if (isMaintenanceMode) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
            <div className="max-w-md w-full text-center">
              <div className="relative mx-auto w-20 h-20 mb-6">
                <div className="absolute inset-0 rounded-full border-3 border-amber-500/20 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-amber-400 rounded-full"></div>
                </div>
                <div className="absolute inset-2 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93s.844.083 1.18-.165l.714-.535a1.133 1.133 0 011.568.13l.773.773c.39.39.44 1.002.13 1.568l-.535.714c-.248.336-.298.776-.165 1.18s.506.71.93.78l.894.149c.542.09.94.56.94 1.11v1.093c0 .55-.398 1.02-.94 1.11l-.894.149c-.424.07-.764.384-.93.78s-.083.844.165 1.18l.535.714a1.133 1.133 0 01-.13 1.568l-.773.773a1.133 1.133 0 01-1.568.13l-.714-.535c-.336-.248-.776-.298-1.18-.165s-.71.506-.78.93l-.149.894c-.09.542-.56.94-1.11.94h-1.093c-.55 0-1.02-.398-1.11-.94l-.149-.894a1.462 1.462 0 00-.78-.93 1.462 1.462 0 00-1.18.165l-.714.535a1.133 1.133 0 01-1.568-.13l-.773-.773a1.133 1.133 0 01-.13-1.568l.535-.714c.248-.336.298-.776.165-1.18a1.462 1.462 0 00-.93-.78l-.894-.149a1.133 1.133 0 01-.94-1.11v-1.093c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.764-.384.93-.78s.083-.844-.165-1.18l-.535-.714a1.133 1.133 0 01.13-1.568l.773-.773a1.133 1.133 0 011.568-.13l.714.535c.336.248.776.298 1.18.165s.71-.506.78-.93l.149-.894z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Under Maintenance</h3>
              <p className="text-slate-400 text-sm mb-6">We&apos;re performing scheduled maintenance. Your data is safe and we&apos;ll be back shortly.</p>
              <button
                onClick={() => window.location.href = '/maintenance?capacitor=true'}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl mb-3"
              >
                Retry Connection
              </button>
              <button
                onClick={() => { if ((window as any).__clearAppDataAndReload) (window as any).__clearAppDataAndReload(); else window.location.reload(); }}
                className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded-xl"
              >
                Clear Cache &amp; Reload
              </button>
            </div>
          </div>
        );
      }

      // Special UI for chunk loading errors
      if (isChunkError || isClearingCaches) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 px-4">
            <div className="max-w-md w-full">
              <BrandedLoadingSpinner
                size="lg"
                message="Updating App..."
                subMessage="A new version is available. Clearing cache and reloading..."
                showSurface
                showProgressBar
                suppressClearCacheButton
              />

              <div className="flex flex-col gap-2 mt-6">
                <button
                  onClick={this.handleClearCacheAndReload}
                  disabled={isClearingCaches}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isClearingCaches ? 'Clearing cache...' : 'Clear Cache & Reload'}
                </button>

                <button
                  onClick={this.handleReload}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Reload Now
                </button>
              </div>
            </div>
          </div>
        );
      }

      const canRetry = retryCount < this.MAX_RETRIES;
      const isRetrying = this.retryTimer !== null;
      const { reportReference, isReporting } = this.state;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Application Error
              </h3>

              {/* Message */}
              <p className="text-sm text-gray-500 mb-6">
                {isRetrying
                  ? `Automatically retrying... (${retryCount + 1}/${this.MAX_RETRIES})`
                  : canRetry
                  ? 'A client-side error occurred. You can retry or reload the app.'
                  : 'Unable to recover automatically. Please reload the app.'}
              </p>

              {/* Report reference */}
              {reportReference ? (
                <div className="mb-6 rounded-lg border-2 border-green-200 bg-green-50 p-4 text-left">
                  <div className="text-sm font-semibold text-green-900 mb-1">✓ Error Reported</div>
                  <p className="text-xs text-green-700 mb-2">Our team has been notified.</p>
                  <div className="rounded border border-green-300 bg-white p-2">
                    <div className="text-xs font-semibold text-green-600 mb-1">Reference Number:</div>
                    <code className="text-sm font-bold text-green-900">{reportReference}</code>
                  </div>
                </div>
              ) : isReporting ? (
                <p className="text-xs text-gray-400 mb-6">Submitting error report…</p>
              ) : null}

              {/* Error details (collapsed) */}
              <details className="text-left mb-6 bg-gray-50 rounded p-3">
                <summary className="text-xs font-medium text-gray-700 cursor-pointer">
                  Technical Details
                </summary>
                <div className="mt-2 space-y-2">
                  <pre className="text-xs text-gray-600 overflow-auto max-h-32 whitespace-pre-wrap break-words">
                    {error.message}
                  </pre>
                  {error.stack && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-1">Stack Trace:</div>
                      <pre className="text-xs text-gray-600 overflow-auto max-h-48 whitespace-pre-wrap break-words">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {canRetry && !isRetrying && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Retry Now
                  </button>
                )}

                <button
                  onClick={this.handleReload}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Reload App
                </button>
              </div>

              {/* Retry counter */}
              {retryCount > 0 && (
                <p className="mt-4 text-xs text-gray-500">
                  Retry attempts: {retryCount}/{this.MAX_RETRIES}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}
